import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AnaliseDesempenhoKeys } from './ad_keys'
import { AdFilterPanePresenter } from './ad_presenter_filter_pane'
import { AnaliseDesempenhoScope, HeaderScope, type FormatoGrafico, type Option } from './ad_scopes'
import { AnaliseDesempenhoService } from './ad_service'
import type { AdFilter, ComparativoValores, Indicador, OpcoesResponse, PainelResponse } from './ad_types'
import {
    formatCompacto,
    formatCompetencia,
    formatData,
    formatDecimal1,
    formatInteiro,
    formatMoeda,
    formatPercentual,
    formatTrimestre,
    formatTrimestreCurto
} from './ad_utils'

const LOG = Logger.get('AD-Presenter')

const service = AnaliseDesempenhoService.singleton()

type Formato = (v: number | null) => string

const pct1: Formato = (v) => formatPercentual(v, 1)
const pct2: Formato = (v) => formatPercentual(v, 2)

const LINHAS: { id: keyof ComparativoValores; rotulo: string; formato?: Formato }[] = [
    { id: 'beneficiarios', rotulo: 'Total Beneficiários', formato: formatInteiro },
    { id: 'ranking', rotulo: 'Ranking de Beneficiários', formato: formatInteiro },
    { id: 'marketShare', rotulo: 'Marketshare', formato: pct1 },
    { id: 'regiaoMaiorMarketShare', rotulo: 'Região com Maior MarketShare' },
    { id: 'marketShareMelhorRegiao', rotulo: 'MarketShare da Melhor Região ANS', formato: pct1 },
    { id: 'municipioMaiorMarketShare', rotulo: 'Município de Maior MarketShare' },
    { id: 'marketShareMelhorMunicipio', rotulo: 'MarketShare do Melhor Município', formato: pct1 },
    { id: 'municipiosAcima100', rotulo: 'Municípios com Mais de 100 Beneficiários', formato: formatInteiro },
    { id: 'pctMedicoHospitalar', rotulo: '% Beneficiários Médico Hospitalares', formato: pct2 },
    { id: 'pctColetivoEmpresarial', rotulo: '% Beneficiários no Plano Coletivo Empresarial', formato: pct1 },
    { id: 'idadeMedia', rotulo: 'Idade do Beneficiário (média)', formato: formatDecimal1 },
    { id: 'pctIdosos', rotulo: '% de Idosos', formato: pct1 },
    { id: 'pctJovens', rotulo: '% de Jovens', formato: pct1 },
    {
        id: 'planosEmComercializacao',
        rotulo: 'Planos Em Comercialização com Beneficiários',
        formato: formatInteiro
    },
    {
        id: 'beneficiariosPorPlano',
        rotulo: 'Beneficiários por Planos em Comercialização (média)',
        formato: formatInteiro
    },
    { id: 'planoMaisComercializado', rotulo: 'Plano Mais Comercializado' },
    { id: 'beneficiariosPlanoTop', rotulo: 'Beneficiários no Plano mais Comercializado', formato: formatInteiro },
    { id: 'receitasMensais', rotulo: 'Receitas Mensais', formato: formatMoeda },
    { id: 'receitasContraprestacaoMensais', rotulo: 'Receitas Mensais de Contraprestação', formato: formatMoeda },
    { id: 'despesasMensais', rotulo: 'Despesas Mensais', formato: formatMoeda },
    { id: 'despesasAssistenciaisMensais', rotulo: 'Despesas Assistenciais Mensais', formato: formatMoeda },
    { id: 'provisoesTecnicasMensais', rotulo: 'Provisões Técnicas Mensais', formato: formatMoeda },
    {
        id: 'resultadoAssistencialPorBeneficiario',
        rotulo: 'Resultado Assistencial mensal por Beneficiário',
        formato: formatMoeda
    },
    { id: 'pctDespesaAssistencial', rotulo: '% Despesa Assistencial em Relação ao Total', formato: pct1 },
    { id: 'receitasPorBeneficiario', rotulo: 'Receitas Mensais por Beneficiário', formato: formatMoeda },
    { id: 'despesaPorBeneficiario', rotulo: 'Despesa Mensal por Beneficiário', formato: formatMoeda },
    { id: 'resultadoPorBeneficiario', rotulo: 'Resultado Por Beneficiário', formato: formatMoeda },
    { id: 'liquidezGeral', rotulo: 'Índice - Liquidez Geral', formato: formatDecimal1 },
    { id: 'liquidezCorrente', rotulo: 'Índice - Liquidez Corrente', formato: formatDecimal1 },
    { id: 'sinistralidade', rotulo: 'Sinistralidade', formato: pct1 }
]

const INDICADORES: (Option<Indicador> & { formato: FormatoGrafico })[] = [
    { value: 'receitas_contraprestacao', label: 'Receitas de Contraprestações', formato: 'moeda' },
    { value: 'despesas_assistenciais', label: 'Despesas Assistenciais', formato: 'moeda' },
    { value: 'liquidez_geral', label: 'Índice de Liquidez Geral', formato: 'indice' },
    { value: 'liquidez_corrente', label: 'Índice de Liquidez Corrente', formato: 'indice' },
    { value: 'sinistralidade', label: 'Índice de Sinistralidade', formato: 'percentual' },
    { value: 'provisoes_tecnicas', label: 'Provisões Técnicas', formato: 'moeda' }
]

export class AnaliseDesempenhoPresenter extends CubePresenter<MainPresenter, AnaliseDesempenhoScope> {
    constructor(app: MainPresenter) {
        super(app, new AnaliseDesempenhoScope())
        this.headerBar = new Presenter(this, this.scope.header)
        this.filterPane = new AdFilterPanePresenter(this, this.scope.filtros)

        const s = this.scope
        for (const child of [
            s.operadoras,
            s.beneficiarios,
            s.liquidezGeral,
            s.liquidezCorrente,
            s.sinistralidade,
            s.comparativo,
            s.grafico
        ]) {
            child.update = this.update
        }
    }

    // :: State

    readonly headerBar: Presenter<HeaderScope, AnaliseDesempenhoPresenter>
    readonly filterPane: AdFilterPanePresenter<AnaliseDesempenhoPresenter>

    private __opcoes?: Promise<OpcoesResponse>
    private __painel?: PainelResponse
    private __painelRequest = 0

    // :: Lifecycle

    override release() {
        this.app.releaseToolbar(this.headerBar)
        this.filterPane.release()
        this.headerBar.release()
        super.release()
        LOG.debug('Finalized')
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        if (!this.app.scope.analiseDesempenhoEnabled) {
            await this.app.flipToDefault()
            return false
        }

        const keys = new AnaliseDesempenhoKeys(this.app, intent)

        this.app.scope.toolbar = this.headerBar.scope
        keys.parentSlot(this.scope)
        keys.adPresenter = this

        if (initialization) {
            await this.__initialize()
        }

        await this.filterPane.catalog.applyParameters(keys, initialization)

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    opcoes(): Promise<OpcoesResponse> {
        if (!this.__opcoes) {
            const opcoes = service.fetchOpcoes()
            opcoes.catch(() => (this.__opcoes = undefined))
            this.__opcoes = opcoes
        }
        return this.__opcoes
    }

    async fetchData() {
        await this.refresh()
    }

    async onSavedFilterApplied() {
        await this.refresh()
    }

    // :: Internals

    private async __initialize() {
        const s = this.scope
        s.grafico.onIndicadorChange = this.action(this.__handleIndicadorChange)

        s.operadoras.caption = 'Operadoras'
        s.operadoras.hint = 'Operadoras ativas no filtro'
        s.beneficiarios.caption = 'Beneficiários Ativos'
        s.beneficiarios.hint = 'Beneficiários de todas as operadoras na UF e cobertura do filtro (última competência)'
        s.liquidezGeral.caption = 'Índice de Liquidez Geral'
        s.liquidezGeral.hint = 'Das operadoras do filtro, somadas, no último balanço'
        s.liquidezCorrente.caption = 'Índice de Liquidez Corrente'
        s.liquidezCorrente.hint = 'Das operadoras do filtro, somadas, no último balanço'
        s.sinistralidade.caption = 'Índice de Sinistralidade'
        s.sinistralidade.hint = 'Despesas assistenciais / contraprestações das operadoras do filtro, no último balanço'

        s.comparativo.caption = 'Comparativo de Resultados'
        s.grafico.caption = 'Indicador'
        s.grafico.opcoes = INDICADORES.map(({ value, label }) => ({ value, label }))

        try {
            await this.opcoes()
        } catch (caught) {
            LOG.error('fetchOpcoes', caught)
            s.erro = this.__mensagemErro(caught)
        }

        await this.filterPane.initialize()
        await this.refresh()
    }

    private buildFilter(): AdFilter {
        return this.filterPane.buildFilter()
    }

    private async refresh() {
        const s = this.scope
        this.filterPane.publish()
        s.appliedFilters = this.filterPane.describeAppliedFilters()

        const requestId = ++this.__painelRequest
        const loadables = [
            s.operadoras,
            s.beneficiarios,
            s.liquidezGeral,
            s.liquidezCorrente,
            s.sinistralidade,
            s.comparativo,
            s.grafico
        ]
        for (const item of loadables) item.loaded = false

        try {
            const painel = await service.fetchPainel(this.buildFilter())
            if (requestId !== this.__painelRequest) return
            s.erro = ''
            this.__painel = painel
            this.__applyPainel(painel)
        } catch (caught) {
            if (requestId !== this.__painelRequest) return
            LOG.error('fetchPainel', caught)
            s.erro = this.__mensagemErro(caught)
        } finally {
            if (requestId === this.__painelRequest) {
                for (const item of loadables) item.loaded = true
            }
        }
    }

    private __applyPainel(painel: PainelResponse) {
        const s = this.scope
        const { titulo, cartoes } = painel

        s.atualizacao = [
            `Atualizado em: ${titulo.atualizadoEm ? formatData(titulo.atualizadoEm) : '-'}`,
            `Competência Contábil: ${titulo.competenciaContabil ? formatTrimestre(titulo.competenciaContabil) : '-'}`,
            `Competência Beneficiários: ${
                titulo.competenciaBeneficiarios ? formatCompetencia(titulo.competenciaBeneficiarios) : '-'
            }`
        ].join('. ')

        const kpi = (scope: typeof s.operadoras, texto: string, detalhe: string) => {
            scope.texto = texto || '—'
            scope.detalhe = detalhe
        }
        kpi(s.operadoras, formatInteiro(cartoes.operadoras), formatInteiro(cartoes.operadoras))
        kpi(s.beneficiarios, formatCompacto(cartoes.beneficiarios), formatInteiro(cartoes.beneficiarios))
        kpi(s.liquidezGeral, formatDecimal1(cartoes.liquidezGeral), String(cartoes.liquidezGeral ?? ''))
        kpi(s.liquidezCorrente, formatDecimal1(cartoes.liquidezCorrente), String(cartoes.liquidezCorrente ?? ''))
        kpi(
            s.sinistralidade,
            formatPercentual(cartoes.sinistralidade).replace(' %', '%'),
            formatPercentual(cartoes.sinistralidade, 4)
        )

        const colunas = painel.colunas
        s.comparativo.operadoras = colunas.map((c) => c.operadora)
        s.comparativo.linhas = LINHAS.map(({ id, rotulo, formato }) => {
            const valores = colunas.map((c) => {
                const v = c.valores[id]
                return typeof v === 'number' && formato ? formato(v) : v === null || v === undefined ? '' : String(v)
            })
            return { id, rotulo, valores, detalhes: valores }
        })
        s.comparativo.vazio = colunas.length
            ? ''
            : 'Escolha as operadoras no filtro "Nome da Operadora" (ou "Registro ANS") para comparar.'

        s.avisoLimite = painel.operadorasForaDoLimite.length
            ? `A tabela e o gráfico mostram até ${painel.limiteOperadoras} operadoras, em ordem alfabética. ` +
              `Ficaram de fora: ${painel.operadorasForaDoLimite.join(', ')}.`
            : ''

        this.__applyGrafico()
    }

    private __applyGrafico() {
        const g = this.scope.grafico
        const painel = this.__painel
        const serie = painel?.series[g.indicador]
        g.formato = INDICADORES.find((i) => i.value === g.indicador)?.formato ?? 'moeda'
        g.datas = (serie?.datas ?? []).map(formatTrimestreCurto)
        g.series = (painel?.colunas ?? []).map((c) => ({
            nome: c.operadora,
            valores: serie?.valores[c.operadora] ?? []
        }))
    }

    private __mensagemErro(caught: unknown) {
        const message = (caught as { message?: string })?.message
        return message ? `Não foi possível carregar os dados: ${message}` : 'Não foi possível carregar os dados.'
    }

    // :: Handlers

    private async __handleIndicadorChange(indicador: Indicador) {
        this.scope.grafico.indicador = indicador
        this.__applyGrafico()
    }
}

export default AnaliseDesempenhoPresenter
